/* eslint-disable react-refresh/only-export-components -- route-table module:
   exports the route arrays (parity-tested) and router bridges alongside the
   view components by design; it is not a fast-refresh boundary. */
import React, { useEffect } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useRoutes,
} from "react-router";
import type { RouteObject } from "react-router";
import { useDispatch } from "react-redux";
import type { UnknownAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import * as Sentry from "@sentry/react";
import ConnectedNavigationBar from "./container/ConnectedNavigationBar";
import { appNavigate, setNavigate } from "./navigation";
import { TitleBridge } from "./pageTitles";
import { logout as logoutAction } from "./userActions";
import { NotFoundView } from "./views/BigMessageView";
import {
  ConnectedLanguageProvider,
  URLBasedLanguageProvider,
} from "../i18n/provider";
import { asyncComponent } from "./util/AsyncComponent";
import { GuardComponent } from "./util/GuardComponent";
import { ErrorView } from "../display/presentation/ErrorView";
import { Attributions } from "./presentation/Attributions";

const AsyncLogin = asyncComponent(
  () => import("./container/ConnectedLoginView"),
);
const AsyncDashboard = asyncComponent(
  () => import("../display/container/ConnectedDashboardView"),
);
const AsyncShop = asyncComponent(
  () => import("../shop/container/ConnectedShopView"),
);
const AsyncShopAdmin = asyncComponent(
  () => import("../shop/container/ConnectedOrderAdminView"),
);
const AsyncShopSubscription = asyncComponent(
  () => import("../shop/container/ConnectedSubscriptionShopView"),
);
const AsyncRegistration = asyncComponent(
  () => import("../settings/container/ConnectedRegistrationView"),
);
const AsyncTimeline = asyncComponent(
  () => import("../display/container/ConnectedScaleOverView"),
);
const AsyncSettings = asyncComponent(
  () => import("../settings/container/ConnectedSettingsView"),
);
const AsyncExport = asyncComponent(
  () => import("../display/container/ConnectedExportView"),
);

const AsyncAdminVariables = asyncComponent(
  () => import("../shop/container/ConnectedConfigEdit"),
);
const AsyncAdminNotificationList = asyncComponent(
  () => import("../display/container/ConnectedNotificationListView"),
);
const AsyncAdminUserList = asyncComponent(
  () => import("../settings/container/ConnectedAdminUserList"),
);
const AsyncAdminControllerList = asyncComponent(
  () => import("../display/container/ConnectedControllerAdminView"),
);
const AsyncAdminControllerCreation = asyncComponent(
  () => import("../settings/container/ConnectedControllerCreation"),
);
const AsyncAdminRegionList = asyncComponent(
  () => import("../settings/container/ConnectedRegionAdminView"),
);
const AsyncJobManagement = asyncComponent(
  () => import("../settings/container/ConnectedJobManagement"),
);
const AsyncFirmwareUpload = asyncComponent(
  () => import("../settings/container/ConnectedFirmwareUpload"),
);

const AsyncManagerControllerList = asyncComponent(
  () => import("../display/container/ConnectedControllerManagerView"),
);
const AsyncAffiliateNotificationView = asyncComponent(
  () => import("../display/container/ConnectedAffiliateNotificationView"),
);
const AsyncManagerUserList = asyncComponent(
  () => import("../settings/container/ConnectedManagerUserList"),
);

const AsyncPasswordRequest = asyncComponent(
  () => import("../settings/container/ConnectedPasswordRequest"),
);
const AsyncPasswordReset = asyncComponent(
  () => import("../settings/container/ConnectedPasswordReset"),
);

const AsyncSharedTimelineFixed = asyncComponent(
  () => import("../display/views/SharedTimelineFixed"),
);
const AsyncSharedTimelineLivefeed = asyncComponent(
  () => import("../display/views/SharedTimelineLivefeed"),
);
const AsyncPublicMap = asyncComponent(
  () => import("../display/views/PublicMapView"),
);
const AsyncPublicTimeline = asyncComponent(
  () => import("../display/container/ConnectedPublicTimelineView"),
);
const AsyncAdminMap = asyncComponent(
  () => import("../display/views/AdminMapView"),
);

/* Installs the router's navigate into the navigation seam so non-component
   code (thunks, api.ts 401 interceptor, chart handlers) keeps navigating. */
export function NavigationBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    // number = history delta (appNavigateBack); react-router's overloads
    // don't accept the string|number union in a single call.
    setNavigate((to, opts) =>
      typeof to === "number" ? navigate(to) : navigate(to, opts),
    );
    return () => setNavigate(null);
  }, [navigate]);
  return null;
}

type LegacyParams = Record<string, string | undefined>;
type LegacyRouterProps = {
  match: { params: LegacyParams };
  location: ReturnType<typeof useLocation>;
};

/* class components that read this.props.match.params / this.props.location
   keep working unchanged behind this bridge */
export function withLegacyRouterProps<P extends object>(
  Component: React.ComponentType<P & LegacyRouterProps>,
  mapParams: (p: LegacyParams) => LegacyParams = (p) => p,
) {
  return function LegacyRouterBridge(props: P) {
    const params = mapParams(useParams());
    const location = useLocation();
    return <Component {...props} match={{ params }} location={location} />;
  };
}

/* v7 forbids dotted params (":controllerId.:channelId"); the URL shape stays,
   the route declares ":combinedId" and this splits it back into the two
   params the legacy consumers expect. */
export const splitCombinedId = ({
  combinedId,
  ...rest
}: LegacyParams): LegacyParams => {
  const [controllerId, channelId] = combinedId ? combinedId.split(".") : [];
  return { ...rest, controllerId, channelId };
};

function LogoutRoute() {
  // store.js is untyped; a thunk-aware dispatch keeps the .then() chain typed.
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  useEffect(() => {
    dispatch(logoutAction())
      .then(() => {
        appNavigate("/", { replace: true });
      })
      .catch(() => console.log("Logout failed"));
  }, [dispatch]);
  return null;
}

const guarded = (element: React.ReactElement) => (
  <GuardComponent>{element}</GuardComponent>
);

export const ADMIN_ROUTES: RouteObject[] = [
  { path: "notifications", element: <AsyncAdminNotificationList /> },
  { path: "users", element: <AsyncAdminUserList /> },
  { path: "controllers", element: <AsyncAdminControllerList /> },
  { path: "controllers/create", element: <AsyncAdminControllerCreation /> },
  { path: "jobs", element: <AsyncJobManagement /> },
  { path: "firmware", element: <AsyncFirmwareUpload /> },
  { path: "variables", element: <AsyncAdminVariables /> },
  { path: "regions", element: <AsyncAdminRegionList /> },
  { path: "map/:area", element: <AsyncAdminMap /> },
  { path: "*", element: <NotFoundView /> },
];

export const MANAGER_ROUTES: RouteObject[] = [
  { path: "users", element: guarded(<AsyncManagerUserList />) },
  { path: "controllers", element: guarded(<AsyncManagerControllerList />) },
  {
    path: "affiliateNotification",
    element: guarded(<AsyncAffiliateNotificationView />),
  },
  { path: "*", element: <NotFoundView /> },
];

const PasswordResetBridge = withLegacyRouterProps(AsyncPasswordReset);

export const FULL_APP_ROUTES: RouteObject[] = [
  { index: true, element: <AsyncLogin /> },
  { path: "password/request", element: <AsyncPasswordRequest /> },
  { path: "password/reset/:token", element: <PasswordResetBridge /> },
  { path: "shop/admin", element: <AsyncShopAdmin /> },
  {
    path: "shop/subscription",
    element: <Navigate to="/subscriptionShop" replace />,
  },
  { path: "shop/abo", element: <Navigate to="/subscriptionShop" replace /> },
  { path: "shop/*", element: <AsyncShop /> },
  { path: "subscriptionShop/*", element: <AsyncShopSubscription /> },
  { path: "dashboard", element: guarded(<AsyncDashboard />) },
  { path: "registration", element: <AsyncRegistration /> },
  { path: "scaleoverview/:expand?", element: guarded(<AsyncTimeline />) },
  { path: "timeline", element: guarded(<AsyncTimeline />) },
  { path: "settings/*", element: guarded(<AsyncSettings />) },
  { path: "a/*", element: guarded(<AdminRoutes />) },
  { path: "m/*", element: guarded(<ManagerRoutes />) },
  { path: "export", element: guarded(<AsyncExport />) },
  // Members-only, exactly as the sharing settings promise ("Community Karte
  // (sichtbar für alle Mitglieder)"). Unguarded, these two were the only
  // non-public routes here without a login check, so an anonymous visitor got
  // a 403 from the API and a blank map rather than the login page (#851).
  // The genuinely public map is the /bs mount in SHARED_BS_ROUTES.
  { path: "community/:area/map", element: guarded(<AsyncPublicMap />) },
  {
    path: "community/:area/timeline/:controllerId?",
    element: guarded(<AsyncPublicTimeline />),
  },
  { path: "logout", element: <LogoutRoute /> },
  { path: "*", element: <NotFoundView /> },
];

export function AdminRoutes() {
  return useRoutes(ADMIN_ROUTES);
}
export function ManagerRoutes() {
  return useRoutes(MANAGER_ROUTES);
}

/*note: the /bs prefix tells api.ts to send a special header to use bienenschweiz permissions. (which is not a security feature, but mostly used to override logged-in users permissions for this map */
export const SHARED_BS_ROUTES: RouteObject[] = [
  { path: ":area/map", element: <AsyncPublicMap /> },
  {
    path: ":area/timeline/:controllerId?",
    element: (
      <>
        <ConnectedNavigationBar />
        <AsyncPublicTimeline />
      </>
    ),
  },
  { path: "*", element: <NotFoundView /> },
];
export const SHARED_SHARED_ROUTES: RouteObject[] = [
  { path: "timeline", element: <AsyncSharedTimelineFixed /> },
  { path: "livefeed", element: <AsyncSharedTimelineLivefeed /> },
  { path: "*", element: <NotFoundView /> },
];

export function FullApp() {
  const routes = useRoutes(FULL_APP_ROUTES);
  return (
    <ConnectedLanguageProvider>
      {/* inside the language provider: useLingui retitles on locale switch */}
      <TitleBridge />
      <ConnectedNavigationBar />
      <Sentry.ErrorBoundary fallback={<ErrorView />}>
        {routes}
      </Sentry.ErrorBoundary>
      {/* inside the language provider (its <Trans> needs i18n context), after
          the routes so the in-flow footer lands at the end of the document */}
      <Attributions />
    </ConnectedLanguageProvider>
  );
}

export function SharedApp({ variant }: { variant: "bs" | "shared" }) {
  const routes = useRoutes(
    variant === "bs" ? SHARED_BS_ROUTES : SHARED_SHARED_ROUTES,
  );
  return (
    <URLBasedLanguageProvider>
      <TitleBridge />
      {routes}
      {/* self-hides on /shared/*; shows on the /bs/* in-flow timeline */}
      <Attributions />
    </URLBasedLanguageProvider>
  );
}

export const TOP_ROUTES: RouteObject[] = [
  { path: "/shared/*", element: <SharedApp variant="shared" /> },
  { path: "/bs/*", element: <SharedApp variant="bs" /> },
  { path: "*", element: <FullApp /> },
];

export function TopRoutes() {
  return useRoutes(TOP_ROUTES);
}
